import { useState, useCallback, useRef, useMemo } from 'react';
import { ContextIntakeScreen, type UploadedFile } from './ContextIntakeScreen';
import { VisibleBreakdownScreen, type ExtractionResult } from './VisibleBreakdownScreen';
import { MissingFieldResolution, type ContextHints } from './MissingFieldResolution';
import { ProcessingScreen } from './ProcessingScreen';
import { supabase } from '@/integrations/supabase/client';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { toast } from '@/hooks/use-toast';
import { logStartFlowEvent } from '@/lib/telemetry/startFlow';
import { canonicalizeIntakeExtraction, type CanonicalError } from '@/lib/intake/canonicalizeIntakeExtraction';
import { mapCanonicalToStore } from '@/lib/intake/mapCanonicalToStore';
import { buildBreakdownState, type BreakdownState } from '@/lib/intake/buildBreakdownState';
import { normalizeExtractionFields } from '@/lib/intake/normalizeDisplayText';
import { applyFieldEdit, applyFieldAccept, applyDilemmaConfirm } from '@/lib/intake/applyFieldEdit';
import { applyResolutionAnswer } from '@/lib/intake/applyResolutionAnswer';
import { buildResolutionQueue } from '@/lib/intake/buildResolutionQueue';

// ── State Machine ──

type IntakePhase = 'input' | 'extracting' | 'revealing' | 'breakdown' | 'resolving' | 'canonicalizing' | 'writingStore' | 'navigating' | 'error';

interface IntakeFlowProps {
  workspaceId: string;
  onComplete: () => void;
  startFlowId: string;
  onSwitchToClassic?: () => void;
}

export const IntakeFlow = ({ workspaceId, onComplete, startFlowId, onSwitchToClassic }: IntakeFlowProps) => {
  // ── Phase ──
  const [phase, setPhase] = useState<IntakePhase>('input');

  // ── Raw input (immutable after extraction) ──
  const [rawInput, setRawInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // ── Extraction result (raw from API, kept for metadata + reveal feed) ──
  const extractionRef = useRef<ExtractionResult | null>(null);
  const [extractionForReveal, setExtractionForReveal] = useState<ExtractionResult | null>(null);
  const extractionStartRef = useRef<number>(0);
  const isPoorExtractionRef = useRef(false);

  // ── BreakdownState: single source of truth during intake ──
  const [breakdown, setBreakdown] = useState<BreakdownState | null>(null);

  // ── Error state ──
  const [lastError, setLastError] = useState<string | null>(null);

  const store = useOnboardingStore();

  // ── File parsing + chunk fetching ──

  const parseUploadedFiles = useCallback(async (files: UploadedFile[]): Promise<Array<{
    document_id: string;
    file_name: string;
    chunk_text: string;
    page_number: number | null;
    chunk_id: string;
  }>> => {
    const uploaded = files.filter(f => f.status === 'uploaded');
    if (uploaded.length === 0) return [];

    const parsePromises = uploaded.map(async (file) => {
      try {
        logStartFlowEvent({ event: 'intake_file_uploaded', workspaceId, metadata: { file_type: file.fileType, file_size: file.fileSize } });
        const { data, error } = await supabase.functions.invoke('parse-intake-document', { body: { document_id: file.id } });
        if (error) throw error;
        if (data?.success) {
          logStartFlowEvent({ event: 'intake_file_parse_success', workspaceId, metadata: { document_id: file.id, chunk_count: data.chunk_count, relevant_count: data.relevant_count } });
          return { id: file.id, success: true };
        } else {
          logStartFlowEvent({ event: 'intake_file_parse_failed', workspaceId, metadata: { document_id: file.id, error: data?.error } });
          return { id: file.id, success: false };
        }
      } catch (err: any) {
        logStartFlowEvent({ event: 'intake_file_parse_failed', workspaceId, metadata: { document_id: file.id, error: err?.message } });
        return { id: file.id, success: false };
      }
    });

    await Promise.all(parsePromises);

    const docIds = uploaded.map(f => f.id);
    const { data: chunks } = await supabase
      .from('session_intake_document_chunks' as any)
      .select('id, document_id, chunk_text, page_number')
      .in('document_id', docIds)
      .eq('is_relevant', true)
      .order('relevance_score', { ascending: false })
      .limit(20);

    if (!chunks || chunks.length === 0) return [];

    const fileMap = new Map(uploaded.map(f => [f.id, f.fileName]));
    logStartFlowEvent({ event: 'intake_chunks_relevant_count', workspaceId, metadata: { count: chunks.length } });

    return (chunks as any[]).map(c => ({
      document_id: c.document_id,
      file_name: fileMap.get(c.document_id) || 'unknown',
      chunk_text: c.chunk_text,
      page_number: c.page_number,
      chunk_id: c.id,
    }));
  }, [workspaceId]);

  // ── EVENT: START_EXTRACTION ──

  const handleSubmit = useCallback(async (content: string) => {
    setRawInput(content);
    setPhase('extracting');
    setExtractionForReveal(null);
    setLastError(null);
    extractionStartRef.current = Date.now();

    logStartFlowEvent({ event: 'intake_submitted', workspaceId, metadata: { length: content.length, file_count: uploadedFiles.filter(f => f.status === 'uploaded').length } });

    try {
      const documentChunks = await parseUploadedFiles(uploadedFiles);
      const requestBody: any = { raw_input: { type: 'freetext', content } };
      if (documentChunks.length > 0) requestBody.document_chunks = documentChunks;

      const { data, error } = await supabase.functions.invoke('extract-decision-context', { body: requestBody });
      if (error) throw error;

      // ── EVENT: EXTRACTION_SUCCEEDED ──
      const result = data as ExtractionResult;
      extractionRef.current = result;
      setExtractionForReveal(result);

      const timeToAnalysis = Date.now() - extractionStartRef.current;
      logStartFlowEvent({
        event: 'intake_extracted', workspaceId,
        metadata: {
          missing_count: result.missing_fields.length,
          field_count: Object.keys(result.fields).length,
          time_to_first_analysis: timeToAnalysis,
          document_chunks_used: documentChunks.length,
        },
      });

      // Build BreakdownState — the single source of truth from here
      const bs = buildBreakdownState(result);
      setBreakdown(bs);

      // Determine if we need resolution first
      const queue = buildResolutionQueue(bs);
      isPoorExtractionRef.current = queue.length > 3;

      // Stay in 'extracting' phase — ContextIntakeScreen will now receive
      // the extraction result and show the reveal feed via ProcessingFeed.
      // The onRevealComplete callback will transition to the next phase.
    } catch (err: any) {
      // ── EVENT: EXTRACTION_FAILED ──
      console.error('Extraction error:', err);
      logStartFlowEvent({ event: 'intake_extraction_failed', workspaceId, metadata: { error: err?.message || 'unknown' } });

      const status = err?.status || err?.context?.status;
      if (status === 429) {
        toast({ title: 'Zu viele Anfragen', description: 'Bitte warte kurz und versuche es erneut.', variant: 'destructive' });
      } else if (status === 402) {
        toast({ title: 'Kontingent erschöpft', description: 'Bitte prüfe dein Konto.', variant: 'destructive' });
      } else {
        toast({ title: 'Extraktion fehlgeschlagen', description: 'Bitte versuche es erneut oder nutze den klassischen Modus.', variant: 'destructive' });
      }
      setLastError(err?.message || 'Extraction failed');
      setPhase('input');
    }
  }, [workspaceId, uploadedFiles, parseUploadedFiles]);

  // ── EVENT: FIELD_EDIT_SAVED ──
  const handleUpdateField = useCallback((key: string, value: string) => {
    logStartFlowEvent({ event: 'intake_field_edited', workspaceId, metadata: { field: key } });
    setBreakdown(prev => prev ? applyFieldEdit(prev, key, value) : prev);
  }, [workspaceId]);

  // ── EVENT: FIELD_EDIT_SAVED (score variant) ──
  const handleUpdateScore = useCallback((key: 'impactScore' | 'urgencyScore', value: number) => {
    handleUpdateField(key, String(value));
  }, [handleUpdateField]);

  // ── EVENT: SCORE_ACCEPTED (user accepts detected value without changing) ── BUG 5 FIX
  const handleAcceptScore = useCallback((key: 'impactScore' | 'urgencyScore') => {
    logStartFlowEvent({ event: 'intake_field_confirmed', workspaceId, metadata: { fields: [key] } });
    setBreakdown(prev => prev ? applyFieldAccept(prev, key) : prev);
  }, [workspaceId]);

  // ── EVENT: FIELD_EDIT_SAVED (preference variant) ──
  const handleUpdatePreference = useCallback((pref: 'a' | 'b' | 'undecided') => {
    handleUpdateField('initialPreference', pref);
  }, [handleUpdateField]);

  // ── EVENT: DILEMMA_CONFIRMED ──
  const handleConfirmDilemma = useCallback(() => {
    logStartFlowEvent({ event: 'intake_field_confirmed', workspaceId, metadata: { fields: ['optionA', 'optionB'] } });
    setBreakdown(prev => prev ? applyDilemmaConfirm(prev) : prev);
  }, [workspaceId]);

  // ── EVENT: RESOLUTION_ANSWERED ──
  const handleResolveField = useCallback((fieldKey: string, value: string | number) => {
    setBreakdown(prev => prev ? applyResolutionAnswer(prev, fieldKey, value) : prev);
  }, []);

  // ── EVENT: RESOLUTION completed → back to breakdown ──
  const handleResolutionComplete = useCallback(() => {
    logStartFlowEvent({ event: 'intake_resolution_completed', workspaceId });
    logStartFlowEvent({ event: 'resolution_complete', workspaceId });
    setPhase('breakdown');
  }, [workspaceId]);

  // ── EVENT: REVEAL_COMPLETE → transition to breakdown or resolving ──
  const handleRevealComplete = useCallback(() => {
    if (!breakdown) return;
    const queue = buildResolutionQueue(breakdown);
    // BUG 3 FIX: Route to resolution if ANY unresolved fields exist (not just priority 1)
    if (queue.length > 0) {
      logStartFlowEvent({ event: 'intake_resolution_started', workspaceId, metadata: { missing: queue.map(q => q.fieldName), is_poor: queue.length > 3 } });
      setPhase('resolving');
    } else {
      setPhase('breakdown');
    }
  }, [breakdown, workspaceId]);

  // ── EVENT: RESOLUTION_CANCELLED ──
  const handleResolutionBack = useCallback(() => {
    setPhase('breakdown');
  }, []);

  // ── EVENT: OPEN_RESOLUTION ──
  const handleOpenResolution = useCallback(() => {
    setPhase('resolving');
  }, []);

  // ── Condense raw extracted text into short system-language hints ──
  const condenseHint = (raw: string | null | undefined, maxLen = 60): string | null => {
    if (!raw) return null;
    let text = String(raw).trim();
    // Strip filler phrases
    text = text
      .replace(/^(ich |wir |man |es |das |die |der |wenn |angesichts von |aufgrund von )/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (text.length <= maxLen) return text;
    // Cut at last word boundary before maxLen
    const truncated = text.slice(0, maxLen).replace(/\s+\S*$/, '');
    return truncated || text.slice(0, maxLen);
  };

  // ── Context hints for resolution questions ──
  const contextHints: ContextHints = useMemo(() => {
    if (!breakdown) return {};
    const f = breakdown.fields;
    return {
      themeHint: condenseHint(f.sessionTitle?.detection_status === 'detected' ? String(f.sessionTitle.value || '') : null, 50),
      goalHint: condenseHint(f.successDefinition?.detection_status === 'detected' ? String(f.successDefinition.value || '') : null, 50),
      timeHorizonHint: f.timeHorizonDays?.detection_status === 'detected' ? `${f.timeHorizonDays.value} Tage` : null,
      recognizedOptionFragment: condenseHint(f.optionA?.detection_status === 'detected' ? String(f.optionA.value || '') : (f.optionB?.detection_status === 'detected' ? String(f.optionB.value || '') : null), 80),
      constraintHint: condenseHint(f.stakesInaction?.detection_status === 'detected' ? String(f.stakesInaction.value || '') : null, 50),
    };
  }, [breakdown]);

  // ── Missing fields derived from breakdown ──
  const missingFieldNames = useMemo(() => {
    if (!breakdown) return [];
    return breakdown.missingFields;
  }, [breakdown]);

  // ── EVENT: START_CANONICALIZATION → WRITE_TO_STORE → NAVIGATE ──

  const handleContinue = useCallback(() => {
    if (!breakdown) return;

    // Dilemma must be confirmed explicitly — don't fall through to resolution
    if (!breakdown.dilemmaConfirmed) {
      toast({ title: 'Bitte bestätige zuerst das Dilemma', description: 'Bestätige die erkannten Optionen, bevor du fortfährst.' });
      return;
    }

    // If not ready, open resolution instead
    if (!breakdown.readiness.canProceedToCanonicalization) {
      setPhase('resolving');
      return;
    }

    // ── CANONICALIZING ──
    setPhase('canonicalizing');

    const result = canonicalizeIntakeExtraction(breakdown);

    if (!result.ok) {
      const errorResult = result as CanonicalError;
      logStartFlowEvent({ event: 'intake_canonicalization_failed' as any, workspaceId, metadata: { fields: errorResult.errors.map(e => e.field) } });
      toast({
        title: 'Nicht alle Pflichtfelder bestätigt',
        description: errorResult.errors[0]?.message,
        variant: 'destructive',
      });
      setPhase('breakdown');
      return;
    }

    if (result.warnings.length > 0) {
      console.info('Canonicalization warnings:', result.warnings);
    }

    // ── WRITING STORE ──
    setPhase('writingStore');

    try {
      const ext = extractionRef.current!;
      mapCanonicalToStore(result.payload, {
        model: ext.model,
        extraction_version: ext.extraction_version,
        classification: ext.classification,
      }, store, rawInput);

      // Telemetry
      const allFields = Object.values(breakdown.fields);
      const docFieldCount = allFields.filter(v => v.source === 'document').length;

      logStartFlowEvent({
        event: 'intake_mapped', workspaceId,
        metadata: {
          confirmed_fields: allFields.filter(v => v.confirmation_status === 'confirmed' || v.confirmation_status === 'edited').length,
          unreviewed_fields: allFields.filter(v => v.confirmation_status === 'unreviewed').length,
          low_confidence_count: allFields.filter(v => v.confidence < 0.6 && v.detection_status === 'detected').length,
          edited_field_count: allFields.filter(v => v.confirmation_status === 'edited').length,
          fields_from_document_count: docFieldCount,
          canonicalization_warnings: result.warnings.length,
        },
      });

      // ── NAVIGATING ──
      setPhase('navigating');
      onComplete();
    } catch (err: any) {
      console.error('Store write failed:', err);
      setLastError(err?.message || 'Store write failed');
      setPhase('error');
      toast({ title: 'Fehler beim Speichern', description: 'Bitte versuche es erneut.', variant: 'destructive' });
    }
  }, [breakdown, store, workspaceId, onComplete]);

  // ── Build extraction-like object for VisibleBreakdownScreen compatibility ──
  const extractionForUI = useMemo((): ExtractionResult | null => {
    if (!breakdown || !extractionRef.current) return null;
    // Merge breakdown field states back into extraction shape
    // Apply client-side normalization as safety net
    const rawFields = Object.fromEntries(
      Object.entries(breakdown.fields).map(([k, v]) => [k, {
        value: v.value,
        confidence: v.confidence,
        source: v.source,
        detection_status: v.detection_status,
        confirmation_status: v.confirmation_status,
        source_snippet: v.source_snippet,
        source_ref: v.source_ref,
        updated_at: v.updated_at,
      }])
    );
    const normalizedFields = normalizeExtractionFields(rawFields);
    return {
      ...extractionRef.current,
      fields: normalizedFields as unknown as Record<string, import('./ExtractionField').ExtractionFieldData>,
      missing_fields: breakdown.missingFields,
      conflicts: breakdown.conflicts,
    };
  }, [breakdown]);

  // ── Render ──

  switch (phase) {
    case 'input':
      return (
        <ContextIntakeScreen
          onSubmit={handleSubmit}
          isExtracting={false}
          initialValue={rawInput}
          uploadedFiles={uploadedFiles}
          onFilesChange={(updater) => setUploadedFiles(typeof updater === 'function' ? updater : () => updater)}
          workspaceId={workspaceId}
          onSwitchToClassic={onSwitchToClassic}
        />
      );

    case 'extracting':
    case 'revealing':
      return (
        <ProcessingScreen
          extraction={extractionForReveal}
          hasFiles={uploadedFiles.some(f => f.status === 'uploaded')}
          onComplete={handleRevealComplete}
          inputQuality={breakdown?.inputQuality}
        />
      );

    case 'resolving':
      return breakdown && extractionForUI ? (
        <MissingFieldResolution
          missingFields={missingFieldNames}
          extractedContext={Object.fromEntries(Object.entries(breakdown.fields).map(([k, v]) => [k, v.value]))}
          contextHints={contextHints}
          onResolve={handleResolveField}
          onComplete={handleResolutionComplete}
          onBack={handleResolutionBack}
          onFieldShown={(f) => logStartFlowEvent({ event: 'missing_field_question_shown', workspaceId, metadata: { field: f } })}
          onFieldResolved={(f) => logStartFlowEvent({ event: 'field_resolved', workspaceId, metadata: { field: f } })}
          onValidationFailed={(f) => logStartFlowEvent({ event: 'answer_validation_failed', workspaceId, metadata: { field: f } })}
          isPoorExtraction={isPoorExtractionRef.current}
        />
      ) : null;

    case 'breakdown':
    case 'canonicalizing':
    case 'writingStore':
      return extractionForUI && breakdown ? (
        <VisibleBreakdownScreen
          extraction={extractionForUI}
          onUpdateField={handleUpdateField}
          onConfirmDilemma={handleConfirmDilemma}
          onUpdateScore={handleUpdateScore}
          onAcceptScore={handleAcceptScore}
          onUpdatePreference={handleUpdatePreference}
          onContinue={handleContinue}
          dilemmaConfirmed={breakdown.dilemmaConfirmed}
          uploadedFiles={uploadedFiles}
        />
      ) : null;

    case 'error':
      return (
        <ContextIntakeScreen
          onSubmit={handleSubmit}
          isExtracting={false}
          initialValue={rawInput}
          uploadedFiles={uploadedFiles}
          onFilesChange={(updater) => setUploadedFiles(typeof updater === 'function' ? updater : () => updater)}
          workspaceId={workspaceId}
          onSwitchToClassic={onSwitchToClassic}
        />
      );

    default:
      return null;
  }
};
